import { useEffect, useRef, useState } from "react";
import { uniq } from "lodash";
import {
  LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
  GET_CIDM_GROUPS,
} from "../../../gql/bookingapi/queries";
import { convertNumericValueToBoolean } from "../../../utils/helpers/data";

const toKey = (value) => (value ? String(value).toLowerCase() : null);

const candidateKeys = (candidate) =>
  [candidate?.userId, candidate?.email, candidate?.key]
    .map(toKey)
    .filter(Boolean);

const getEquipmentAccessData = (item = {}) => {
  const addInfo = item?.addInfo || {};
  const inventoryId = item?.inventoryId || addInfo?.inventoryId || item?.id || null;
  const isBookingControlledRaw =
    addInfo?.isBookingControlled
    ?? item?.isBookingControlled
    ?? item?.is_booking_controlled;
  const bookableForUsers =
    addInfo?.bookableForUsers
    ?? item?.bookableForUsers
    ?? item?.bookable_for_users
    ?? [];
  const cidmGroupName =
    addInfo?.cidmGroupName
    ?? addInfo?.cidm_group_name
    ?? item?.cidmGroupName
    ?? item?.cidm_group_name
    ?? null;

  return {
    inventoryId,
    isBookingControlled: convertNumericValueToBoolean(isBookingControlledRaw),
    bookableForUsers,
    cidmGroupName,
  };
};

/**
 * Resolves, for the currently selected equipment, which users are eligible to
 * be picked as "Booking For" -- i.e. the intersection of each controlled
 * equipment's allow-list (direct `bookableForUsers` plus, when resolvable, its
 * CIDM group membership via `GET_CIDM_GROUPS`). Equipment that isn't booking
 * controlled imposes no restriction. Callers should bypass this hook's result
 * for admins (RETINA-7943173 AC2 allows admins to book for anyone).
 *
 * @param {Array} instrumentDetails - Selected equipment (redux
 *   `bookingInstruments` shape), each with `inventoryId` and
 *   `addInfo.isBookingControlled`/`addInfo.bookableForUsers`.
 * @param {Object} client - Apollo client (for on-demand access-config/CIDM
 *   lookups, cached per inventoryId/groupName for the component's lifetime).
 * @returns {{ isUserEligible: (candidate: object) => boolean, isFiltering:
 *   boolean }}
 */
export const useEligibleBookingForUsers = (instrumentDetails, client) => {
  const accessConfigCacheRef = useRef(new Map());
  const cidmGroupCacheRef = useRef(new Map());
  const [eligibleUserKeys, setEligibleUserKeys] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const resolveAccessConfig = async (inventoryId, bookableForUsers, fallbackCidmGroupName) => {
      if (!inventoryId) {
        return {
          bookableForUsers: bookableForUsers || [],
          cidmGroupName: fallbackCidmGroupName || null,
        };
      }

      if (accessConfigCacheRef.current.has(inventoryId)) {
        return accessConfigCacheRef.current.get(inventoryId);
      }

      let resolved = {
        bookableForUsers: bookableForUsers || [],
        cidmGroupName: fallbackCidmGroupName || null,
      };
      try {
        const { data } = await client.query({
          query: LIST_BOOKING_ACCESS_CONFIGURATION_BY_EQUIPMENT,
          variables: {
            limit: 20,
            modelName: "BOOKINGACCESSCONFIG",
            inventoryId: { eq: inventoryId },
          },
          fetchPolicy: "no-cache",
        });
        const configItems = data?.listBookingAccessConfigurationByEquipment?.items || [];
        const groups = configItems.flatMap((config) => config?.groups?.items || []);
        const directUserEmails = groups.flatMap((group) => (group?.users || []).map((u) => u.email));
        const cidmGroupName = groups.find((group) => group?.cidmGroup?.groupName)?.cidmGroup?.groupName || fallbackCidmGroupName || null;
        resolved = {
          bookableForUsers: uniq([...(bookableForUsers || []), ...directUserEmails]),
          cidmGroupName,
        };
      } catch (err) {
        // Access config lookup failed -- fall back to whatever addInfo already had.
        console.error("Error loading booking access config for", inventoryId, err);
      }

      accessConfigCacheRef.current.set(inventoryId, resolved);
      return resolved;
    };

    const resolveCidmGroupKeys = async (cidmGroupName) => {
      if (!cidmGroupName) return null;
      if (cidmGroupCacheRef.current.has(cidmGroupName)) {
        return cidmGroupCacheRef.current.get(cidmGroupName);
      }

      let keys = null;
      try {
        const { data } = await client.query({
          query: GET_CIDM_GROUPS,
          variables: { input: { groupName: cidmGroupName } },
          fetchPolicy: "no-cache",
        });
        const members = data?.getCidmGroups || [];
        keys = new Set(members.flatMap(candidateKeys));
      } catch (err) {
        // CIDM lookup failed -- don't exclude candidates for this equipment,
        // rely on backend validation as the authoritative gate.
        console.error("Error loading CIDM group members for", cidmGroupName, err);
      }

      cidmGroupCacheRef.current.set(cidmGroupName, keys);
      return keys;
    };

    const resolve = async () => {
      const controlledEquipment = (instrumentDetails || [])
        .map(getEquipmentAccessData)
        .filter((item) => item?.isBookingControlled && item?.inventoryId);

      if (controlledEquipment.length === 0) {
        if (!cancelled) setEligibleUserKeys(null);
        return;
      }

      const allowLists = await Promise.all(
        controlledEquipment.map(async (item) => {
          const { bookableForUsers, cidmGroupName } = await resolveAccessConfig(
            item.inventoryId,
            item.bookableForUsers,
            item.cidmGroupName,
          );
          const cidmGroupKeys = await resolveCidmGroupKeys(cidmGroupName);
          const directKeys = new Set((bookableForUsers || []).map(toKey).filter(Boolean));
          const hasCidmGroupKeys = !!cidmGroupKeys && cidmGroupKeys.size > 0;

          if (directKeys.size === 0 && !hasCidmGroupKeys) {
            // Nothing resolvable for this equipment (including an empty/unresolved
            // CIDM group) -- don't exclude anyone.
            return null;
          }

          return hasCidmGroupKeys ? new Set([...directKeys, ...cidmGroupKeys]) : directKeys;
        }),
      );

      if (cancelled) return;

      const usableAllowLists = allowLists.filter(Boolean);
      if (usableAllowLists.length === 0) {
        setEligibleUserKeys(null);
        return;
      }

      const [first, ...rest] = usableAllowLists;
      const intersection = new Set(
        [...first].filter((key) => rest.every((set) => set.has(key))),
      );
      setEligibleUserKeys(intersection);
    };

    resolve();

    return () => {
      cancelled = true;
    };
  }, [instrumentDetails, client]);

  const isUserEligible = (candidate) => {
    if (eligibleUserKeys === null) return true;
    return candidateKeys(candidate).some((key) => eligibleUserKeys.has(key));
  };

  return { isUserEligible, isFiltering: eligibleUserKeys !== null, eligibleUserKeys };
};
